// DiscoverFlixRow.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { useInView } from 'react-intersection-observer';

import FlixCard from '../container/FlixCard.js';
import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import HydrationBoundary from '../component/HydrationBoundary.jsx';
import { getIosVersion } from '../resource/getUserAgent.js';
import { freezoneFeed, freezoneType } from '../resource/freezoneConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId } from '../resource/trackingConstants.js';

import MoreIconSrc from '../../img/ic_arrow_right_white.svg';
import { color } from '../style/variables.js';
import media from '../style/media.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { flixRowAutoPlayWidth } from '../style/variables.js';
import { ITEM_COUNT_EAGER_RENDER } from '../resource/feedConstants.js';

const isServer = typeof window === 'undefined';

export class DiscoverFlixRow extends React.PureComponent {
  state = {
    shouldAutoPlay: false,
    isFirstFrame: true,
  };
  nextTicks = [];
  messagesElement = undefined;
  setShouldAutoPlay = debounce(() => {
    this.setState({
      shouldAutoPlay: window.screen.width < flixRowAutoPlayWidth,
    });
  }, 500);
  handleMessagesScroll = debounce(() => {
    const { category, setScrollLeft } = this.props;
    if (this.messagesElement) {
      setScrollLeft({
        scrollLeft: this.messagesElement.scrollLeft,
        category,
      });
    }
  }, 500);
  componentDidMount() {
    this.nextTicks.push(
      setTimeout(() => this.setState({ isFirstFrame: false }))
    );
    this.setShouldAutoPlay();
    this.initDiscoverFlixRow();
    window.addEventListener('resize', this.setShouldAutoPlay);
  }
  componentDidUpdate(prevProps) {
    const {
      isAuthed,
      shouldRefresh,
      category,
      fetchFeeds,
      scrollLeft,
      feedUnixTimestamp,
    } = this.props;
    if (
      isAuthed !== prevProps.isAuthed ||
      feedUnixTimestamp !== prevProps.feedUnixTimestamp
    ) {
      this.nextTicks.push(
        setTimeout(
          () =>
            fetchFeeds({
              page: 1,
              type: category,
              unixTimestamp: feedUnixTimestamp,
            }),
          0
        )
      );
    }
    if (shouldRefresh && shouldRefresh !== prevProps.shouldRefresh) {
      fetchFeeds({
        page: 1,
        type: category,
        shouldSkipLocalCache: shouldRefresh,
        unixTimestamp: feedUnixTimestamp,
      });
    }
    if (this.messagesElement && scrollLeft > this.messagesElement.scrollLeft) {
      this.messagesElement.scrollLeft = scrollLeft;
    }
  }
  componentWillUnmount() {
    this.setShouldAutoPlay.cancel();
    window.removeEventListener('resize', this.setShouldAutoPlay);
    this.nextTicks.forEach(clearTimeout);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'DiscoverFlixRow';
    }
  }

  initDiscoverFlixRow = () => {
    const { isFetched, fetchFeeds, category, feedUnixTimestamp } = this.props;

    if (!isFetched) {
      this.nextTicks.push(
        setTimeout(() => {
          fetchFeeds({
            page: 1,
            type: category,
            unixTimestamp: feedUnixTimestamp,
          });
        }, 0)
      );
    }
  };

  checkItemShouldEagerRender = index => {
    const { shouldEagerRender, itemCountEagerRender } = this.props;
    return shouldEagerRender && index < itemCountEagerRender;
  };

  renderHydrateFlixCard = ({
    messageId,
    category,
    index,
    isProfileCategory,
    shouldShowUsername,
  }) => {
    const { shouldAutoPlay } = this.state;
    const { shouldPlayFeedId } = this.props;

    return (
      <ObservableHydrateFlixCard
        key={messageId}
        messageId={messageId}
        category={category}
        index={index}
        isProfileCategory={isProfileCategory}
        shouldShowUsername={shouldShowUsername}
        shouldAutoPlay={shouldAutoPlay}
        shouldPlayFeedId={shouldPlayFeedId}
        onClick={this.handleCardClick}
        shouldEagerRender={this.checkItemShouldEagerRender(index)}
      />
    );
  };

  renderFlixCard = ({
    messageId,
    category,
    index,
    isProfileCategory,
    shouldShowUsername,
  }) => {
    const { shouldAutoPlay } = this.state;
    const { shouldPlayFeedId } = this.props;

    return (
      <ObservableFlixCard
        key={messageId}
        messageId={messageId}
        category={category}
        index={index}
        isProfileCategory={isProfileCategory}
        shouldShowUsername={shouldShowUsername}
        shouldAutoPlay={shouldAutoPlay}
        shouldPlayFeedId={shouldPlayFeedId}
        onClick={this.handleCardClick}
        shouldEagerRender={this.checkItemShouldEagerRender(index)}
      />
    );
  };

  renderPlaceholders = () => {
    return (
      <MessagesWrapper>
        <Messages>
          {Array(10)
            .fill()
            .map((elem, index) => (
              <FlixCardWrapper key={index}>
                <FlixCard messageId={null} shouldShowAvatar />
              </FlixCardWrapper>
            ))}
        </Messages>
      </MessagesWrapper>
    );
  };

  renderListContent = () => {
    const {
      messageIds,
      nextPage,
      isFetching,
      fetchFeeds,
      category,
      isProfileCategory,
      feedUnixTimestamp,
      shouldShowUsername,
    } = this.props;
    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    const renderFlixCard = shouldHydrate
      ? this.renderHydrateFlixCard
      : this.renderFlixCard;
    const renderMessages = () =>
      messageIds.length
        ? messageIds.map((messageId, index) =>
            renderFlixCard({
              messageId,
              category,
              index,
              isProfileCategory,
              shouldShowUsername,
            })
          )
        : Array(10)
            .fill()
            .map((elem, index) => (
              <FlixCardWrapper key={index}>
                <FlixCard messageId={null} shouldShowAvatar />
              </FlixCardWrapper>
            ));

    return (
      <MessageScroller
        onScroll={this.handleMessagesScroll}
        ref={el => (this.messagesElement = el)}
      >
        <InfiniteScroller
          axis="x"
          useWindow={false}
          pageStart={1}
          hasMore={nextPage !== null}
          loadMore={() =>
            fetchFeeds({
              page: nextPage,
              type: category,
              unixTimestamp: feedUnixTimestamp,
            })
          }
          isFetching={isFetching}
          threshold={100}
        >
          <MessagesWrapper>
            <Messages>
              <ScrollItem
                loader={
                  <FlixCardWrapper>
                    <FlixCard shouldShowAvatar />
                  </FlixCardWrapper>
                }
              >
                {renderMessages()}
              </ScrollItem>
            </Messages>
          </MessagesWrapper>
        </InfiniteScroller>
      </MessageScroller>
    );
  };

  render() {
    const {
      t,
      title,
      isFetched,
      shouldShowMoreLink,
      shouldShowTotalCount,
      totalCount,
      isProfileCategory,
      isTitleClickable,
      titleI18nId,
      titleLink,
      index,
      category,
      messageIds,
    } = this.props;
    if (messageIds.length === 0 && isFetched) {
      return null;
    }
    return (
      <StyledDiscoverFlixRow>
        <Header>
          <HeaderLeft>
            <Title
              to={titleLink}
              as={isTitleClickable ? Link : undefined}
              replace={isProfileCategory}
            >
              <TitleText>{title}</TitleText>
              {shouldShowTotalCount && (
                <TotalCount>({totalCount || 0})</TotalCount>
              )}
            </Title>
          </HeaderLeft>
          <HeaderRight>
            {shouldShowMoreLink && (
              <CategoryMoreButton
                to={
                  category === freezoneFeed.AMATEUR
                    ? `/freezone/${freezoneType.AMATEUR}`
                    : `/flix-categories/${category}`
                }
                data-element_id={ButtonId.All.ButtonMore}
                data-tracking_payload={{
                  'discover.index': index,
                  'discover.category': category,
                }}
                title={t(titleI18nId, {
                  ns: TranslationNamespace.FEED,
                })}
              >
                <MoreButtonText>
                  {t('more', { ns: TranslationNamespace.GENERAL })}
                </MoreButtonText>
                <MoreButtonIcon
                  src={MoreIconSrc}
                  aria-hidden
                  alt={t('more', { ns: TranslationNamespace.GENERAL })}
                />
              </CategoryMoreButton>
            )}
          </HeaderRight>
        </Header>
        {this.renderListContent()}
      </StyledDiscoverFlixRow>
    );
  }
}

DiscoverFlixRow.propTypes = {
  t: PropTypes.func.isRequired,
  title: PropTypes.string,
  titleI18nId: PropTypes.string,
  titleLink: PropTypes.string,
  category: PropTypes.string,
  shouldPlayFeedId: PropTypes.string,
  fetchFeeds: PropTypes.func,
  isAuthed: PropTypes.bool,
  isFetched: PropTypes.bool,
  isFetching: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  shouldShowMoreLink: PropTypes.bool,
  shouldShowTotalCount: PropTypes.bool,
  shouldShowUsername: PropTypes.bool,
  isProfileCategory: PropTypes.bool,
  isTitleClickable: PropTypes.bool,
  messageIds: PropTypes.array,
  index: PropTypes.number,
  nextPage: PropTypes.number,
  totalCount: PropTypes.number,
  setScrollLeft: PropTypes.func,
  scrollLeft: PropTypes.number,
  feedUnixTimestamp: PropTypes.number,
  shouldEagerRender: PropTypes.bool,
  itemCountEagerRender: PropTypes.number,
};

DiscoverFlixRow.defaultProps = {
  title: '',
  isAuthed: false,
  isFetching: false,
  shouldRefresh: false,
  shouldShowMoreLink: true,
  shouldShowTotalCount: false,
  shouldShowUsername: false,
  isProfileCategory: false,
  isTitleClickable: false,
  isFetched: false,
  titleI18nId: '',
  titleLink: '',
  category: '',
  shouldPlayFeedId: '',
  fetchFeeds: () => null,
  messageIds: [],
  index: null,
  nextPage: null,
  totalCount: 0,
  setScrollLeft: () => null,
  scrollLeft: 0,
  feedUnixTimestamp: 0,
  shouldEagerRender: false,
  itemCountEagerRender: ITEM_COUNT_EAGER_RENDER,
};

const OBSERVER_OPTIONS = {
  rootMargin: '10%',
  threshold: [0, 1],
};

const ObservableHydrateFlixCard = ({
  messageId,
  category,
  index,
  isProfileCategory,
  shouldShowUsername,
  shouldAutoPlay,
  shouldPlayFeedId,
  onClick,
  shouldEagerRender,
}) => {
  const { ref, inView, entry } = useInView(OBSERVER_OPTIONS);
  const intersectionRatio = entry ? entry.intersectionRatio : 0;
  const shouldPlay =
    shouldAutoPlay && intersectionRatio >= 1 && shouldPlayFeedId === category;

  if (shouldEagerRender) {
    return (
      <FlixCardWrapper ref={ref} onClick={onClick}>
        <FlixCard
          messageId={messageId}
          shouldPlay={shouldPlay}
          categoryId={category}
          categoryIndex={index}
          itemIndexInCategory={index}
          shouldTrackEvent
          shouldShowTooltip
          shouldShowAvatar={!isProfileCategory}
          shouldShowUsername={shouldShowUsername || !isProfileCategory}
          shouldUseLazyImage={false}
          shouldAnimateLoader={inView}
          isIntersecting={inView}
        />
      </FlixCardWrapper>
    );
  }

  return (
    <HydrationBoundary
      shouldHydrate={inView}
      wrapper={<FlixCardWrapper ref={ref} onClick={onClick} />}
    >
      <FlixCard
        messageId={messageId}
        shouldPlay={shouldPlay}
        categoryId={category}
        categoryIndex={index}
        itemIndexInCategory={index}
        shouldTrackEvent
        shouldShowTooltip
        shouldShowAvatar={!isProfileCategory}
        shouldShowUsername={shouldShowUsername || !isProfileCategory}
        shouldUseLazyImage={false}
        shouldAnimateLoader={inView}
        isIntersecting={inView}
      />
    </HydrationBoundary>
  );
};

const ObservableFlixCard = ({
  messageId,
  category,
  index,
  isProfileCategory,
  shouldShowUsername,
  shouldAutoPlay,
  shouldPlayFeedId,
  onClick,
  shouldEagerRender,
}) => {
  const { ref, inView, entry } = useInView(OBSERVER_OPTIONS);

  const intersectionRatio = entry ? entry.intersectionRatio : 0;
  const shouldPlay =
    shouldAutoPlay && intersectionRatio >= 1 && shouldPlayFeedId === category;

  if (shouldEagerRender) {
    return (
      <FlixCardWrapper ref={ref} onClick={onClick}>
        <FlixCard
          messageId={messageId}
          shouldPlay={shouldPlay}
          categoryId={category}
          categoryIndex={index}
          itemIndexInCategory={index}
          shouldTrackEvent
          shouldShowTooltip
          shouldShowAvatar={!isProfileCategory}
          shouldShowUsername={shouldShowUsername || !isProfileCategory}
          shouldUseLazyImage={false}
          shouldAnimateLoader={inView}
          isIntersecting={inView}
        />
      </FlixCardWrapper>
    );
  }

  if (inView) {
    return (
      <FlixCardWrapper ref={ref} onClick={onClick}>
        <FlixCard
          messageId={messageId}
          shouldPlay={shouldPlay}
          categoryId={category}
          categoryIndex={index}
          itemIndexInCategory={index}
          shouldTrackEvent
          shouldShowTooltip
          shouldShowAvatar={!isProfileCategory}
          shouldShowUsername={shouldShowUsername || !isProfileCategory}
          shouldUseLazyImage={false}
          shouldAnimateLoader={inView}
          isIntersecting={inView}
        />
      </FlixCardWrapper>
    );
  }

  return (
    <FlixCardWrapper ref={ref} key={index}>
      <FlixCard messageId={null} shouldShowAvatar />
    </FlixCardWrapper>
  );
};

ObservableHydrateFlixCard.propTypes = {
  messageId: PropTypes.string,
  category: PropTypes.string,
  index: PropTypes.number,
  isProfileCategory: PropTypes.bool,
  shouldShowUsername: PropTypes.bool,
  shouldAutoPlay: PropTypes.bool,
  shouldPlayFeedId: PropTypes.string,
  onClick: PropTypes.func,
  shouldEagerRender: PropTypes.bool,
};

ObservableFlixCard.propTypes = {
  messageId: PropTypes.string,
  category: PropTypes.string,
  index: PropTypes.number,
  isProfileCategory: PropTypes.bool,
  shouldShowUsername: PropTypes.bool,
  shouldAutoPlay: PropTypes.bool,
  shouldPlayFeedId: PropTypes.string,
  onClick: PropTypes.func,
  shouldEagerRender: PropTypes.bool,
};

// As of 2024.10, the height of StyledDiscoverFlixRow shouldn't be less than 308px (320px on mobile);
// otherwise, it would make withViewportItemTracker out of function.
// If the height is too short, it causes the `FlixCard` to be obscured,
// which prevents the Intersection Observer from triggering `isIntersecting = true`
const StyledDiscoverFlixRow = styled.article`
  background-color: ${color.black};
  height: 100%;
  min-height: 372px;
  ${media.tablet`
    min-height: 274px;
  `};
  ${media.mobile`
    min-height: 360px;
  `};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  padding: 0 16px;
  justify-content: space-between;
  align-items: center;
  ${media.mobile`
    padding: 0px 16px;
  `};
`;

const HeaderLeft = styled.div`
  overflow: hidden;
  display: flex;
  align-items: flex-start;
`;

const HeaderRight = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  min-width: max-content;
  margin-left: 8px;
`;

const CategoryMoreButton = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${color.white};
`;

const MoreButtonText = styled.span`
  margin-right: 4px;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 600;
  ${media.mobile`
    font-size: 12px;
  `}
`;

const MoreButtonIcon = styled.img`
  height: 16px;
  width: 16px;
`;

const Title = styled.h2`
  overflow: hidden;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  flex: 1;
  display: flex;
  align-items: center;
  text-align: justify;
  line-height: 1.5;
  ${media.mobile`
    font-size: 18px;
  `}
`;

const TitleText = styled.div`
  ${TextEllipsis};
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  ${media.mobile`
      font-size: 18px;
  `}
`;

const TotalCount = styled.span`
  padding-left: 8px;
  color: var(--font-color-primary-on-dark, ${color.white});
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.014px;
  ${media.mobile`
    font-size: 12px;
    letter-spacing: 0.012px;
  `}
`;

// On iOS 13, scrolling momentum is applied to elements with scrollbar automatically,
// and "-webkit-overflow-scrolling: touch;" causes rendering bug, so the attribute is not necessary.
// On iOS 12 and below, "-webkit-overflow-scrolling: touch;" is necessary for momentum scrolling.
const MessageScroller = styled.div`
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  ${getIosVersion().major < 13 ? '-webkit-overflow-scrolling: touch;' : ''}

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const MessagesWrapper = styled.div`
  display: inline-block;
  height: 100%;
`;

const Messages = styled.div`
  padding: 4px 16px 0px;
  display: flex;
  height: 100%;
`;

const FlixCardWrapper = styled.div`
  flex: none;
  margin-right: 12px;
  border-radius: 8px;
  width: 343px;
  height: 100%
  :last-child {
    margin-right: 0;
  }
  ${media.tablet`
    margin-right: 8px;
    width: 216px;
  `}
  ${media.mobile`
    width: 328px;
  `};
`;

export default withTranslation()(DiscoverFlixRow);
